// frontend/src/components/pages/admin/usage/IndividualsUsageTab.jsx
import React, { useEffect, useState } from "react";
import {
  getUsageByRole,
  getUsageByUserId,
  downloadInvoicePdf,
} from "../../../../api/apiFunction/usageServices";
import { 
  getAllIndividualUsersPaginated,
  getUserUsageAndPricing,
  getStripeInvoices
} from "../../../../api/apiFunction/userServices";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  ArrowDownToLine,
  Download,
  FileText,
  Loader2,
} from "lucide-react";
import UsageStatsCards from "./UsageStatsCards";
import "../../../../styles/UsageCards.css";

const PAGE_SIZE = 10;

// Invoice Download Button Component
const InvoiceDownloadButton = ({ userId, invoices, loading, onLoadInvoices }) => {
  const [showDropdown, setShowDropdown] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownload = (invoiceUrl) => {
    if (invoiceUrl) {
      window.open(invoiceUrl, "_blank");
    }
  };

  const hasInvoices = invoices && invoices.length > 0;

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-bg-40 text-fg-70 hover:bg-bg-50 hover:text-fg-90 border border-bd-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
        onClick={() => {
          if (!invoices && !loading) {
            onLoadInvoices();
          }
          setShowDropdown(!showDropdown);
        }}
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            <span>Loading...</span>
          </>
        ) : (
          <>
            <FileText size={14} className="flex-shrink-0" />
            <span className="whitespace-nowrap">
              {hasInvoices ? `${invoices.length} Invoice${invoices.length !== 1 ? "s" : ""}` : "Invoices"}
            </span>
            <ChevronDown size={12} className={`flex-shrink-0 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {showDropdown && hasInvoices && (
        <div className="absolute right-0 mt-2 w-72 rounded-lg shadow-xl bg-bg-60 border border-bd-50 z-50 max-h-96 overflow-y-auto">
          <div className="py-1.5">
            {invoices.map((invoice) => (
              <button
                key={invoice.id}
                className="w-full text-left px-4 py-2.5 text-sm text-fg-50 hover:bg-bg-40 active:bg-bg-30 flex items-center justify-between transition-colors duration-150 border-b border-bd-50 last:border-b-0"
                onClick={() => handleDownload(invoice.invoicePdf || invoice.hostedInvoiceUrl)}
              >
                <div className="flex-1 min-w-0 pr-3">
                  <div className="font-medium truncate text-fg-70">
                    {invoice.number || invoice.id}
                  </div>
                  <div className="text-xs text-fg-60 mt-0.5">
                    ${invoice.amount.toFixed(2)} {invoice.currency} • {new Date(invoice.created).toLocaleDateString()}
                  </div>
                </div>
                <Download size={16} className="ml-2 flex-shrink-0 text-fg-60 hover:text-fg-80 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      )}

      {showDropdown && !hasInvoices && !loading && (
        <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-xl bg-bg-60 border border-bd-50 z-50">
          <div className="py-3 px-4 text-sm text-fg-60 text-center">
            No invoices found
          </div>
        </div>
      )}
    </div>
  );
};

const UsageBentoCard = ({ loading, error, usage }) => {
  const [statsTab, setStatsTab] = useState("daily");

  const formatDate = (dateString) => {
    if (!dateString) return "No activity";
    const date = new Date(dateString);
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-US", options);
  };

  return (
    <div className="bg-bg-60 p-6 border border-bd-50">
      {loading ? (
        <div className="flex items-center justify-center h-24">
          Loading usage...
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-24 text-red-500">
          {error}
        </div>
      ) : usage ? (
        <>
          <div className="text-xs text-fg-60 uppercase mb-2">USAGE SUMMARY</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full mb-6">
            <div className="bg-bg-30 rounded-lg p-4 w-full flex flex-col items-center justify-center">
              <div className="text-xs text-fg-60 mb-1">Total Tokens</div>
              <div className="text-lg font-semibold text-fg-50">
                {(
                  (usage.totals?.highEndTokens || 0) +
                  (usage.totals?.smallTokens || 0)
                ).toLocaleString()}
              </div>
            </div>
            <div className="bg-bg-30 rounded-lg p-4 w-full flex flex-col items-center justify-center">
              <div className="text-xs text-fg-60 mb-1">Total Cost</div>
              <div className="text-lg font-semibold text-fg-50">
                ${usage.totals?.totalCost?.toFixed(6) || "0.00"}
              </div>
            </div>
            <div className="bg-bg-30 rounded-lg p-4 w-full flex flex-col items-center justify-center">
              <div className="text-xs text-fg-60 mb-1">Total Requests</div>
              <div className="text-lg font-semibold text-fg-50">
                {usage.totals?.totalRequests?.toLocaleString() || 0}
              </div>
            </div>
            <div className="bg-bg-30 rounded-lg p-4 w-full flex flex-col items-center justify-center">
              <div className="text-xs text-fg-60 mb-1">Last Activity</div>
              <div className="text-sm font-semibold text-fg-50 text-center">
                {usage.lastActive
                  ? formatDate(usage.lastActive)
                  : "No activity"}
              </div>
            </div>
          </div>
          {/* Stats Tabs */}
          <div className="text-xs text-fg-60 uppercase mb-4">USAGE HISTORY</div>
          <div className="flex gap-2 mb-4">
            <button
              className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                statsTab === "daily"
                  ? "bg-bg-40 text-fg-90 shadow-sm"
                  : "bg-transparent text-fg-60 hover:text-fg-80"
              }`}
              onClick={() => setStatsTab("daily")}
            >
              Daily
            </button>
            <button
              className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                statsTab === "weekly"
                  ? "bg-bg-40 text-fg-90 shadow-sm"
                  : "bg-transparent text-fg-60 hover:text-fg-80"
              }`}
              onClick={() => setStatsTab("weekly")}
            >
              Weekly
            </button>
            <button
              className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                statsTab === "monthly"
                  ? "bg-bg-40 text-fg-90 shadow-sm"
                  : "bg-transparent text-fg-60 hover:text-fg-80"
              }`}
              onClick={() => setStatsTab("monthly")}
            >
              Monthly
            </button>
          </div>

          {/* Stats Table */}
          <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-bd-50">
              <thead className="bg-bg-40">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    {statsTab === "daily"
                      ? "Date"
                      : statsTab === "weekly"
                      ? "Week Range"
                      : "Month"}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Requests
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody className="bg-bg-60 divide-y divide-bd-50">
                {(statsTab === "daily"
                  ? usage.daily
                  : statsTab === "weekly"
                  ? usage.weekly
                  : usage.monthly) &&
                (statsTab === "daily"
                  ? usage.daily
                  : statsTab === "weekly"
                  ? usage.weekly
                  : usage.monthly
                ).length > 0 ? (
                  (statsTab === "daily"
                    ? usage.daily
                    : statsTab === "weekly"
                    ? usage.weekly
                    : usage.monthly
                  ).map((stat, idx) => (
                    <tr key={idx}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        {statsTab === "daily"
                          ? stat.date
                          : statsTab === "weekly"
                          ? stat.weekRange
                          : stat.month}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        {stat.requests?.toLocaleString() || "0"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        {(
                          (stat.highEndTokens || 0) + (stat.smallTokens || 0)
                        ).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        ${stat.cost?.toFixed(6) || "0.000000"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-6 py-4 text-center text-sm text-fg-60"
                    >
                      No {statsTab} stats found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
};

const IndividualsUsageTab = () => {
  const [tab, setTab] = useState("overall");
  const [statsTab, setStatsTab] = useState("daily"); // 'daily' | 'weekly' | 'monthly'
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [perUserUsage, setPerUserUsage] = useState({});
  const [perUserLoading, setPerUserLoading] = useState({});
  const [perUserError, setPerUserError] = useState({});
  const [perUserEmailLoading, setPerUserEmailLoading] = useState({});
  const [perUserAccurateUsage, setPerUserAccurateUsage] = useState({});
  const [perUserInvoices, setPerUserInvoices] = useState({});
  const [perUserInvoicesLoading, setPerUserInvoicesLoading] = useState({});

  useEffect(() => {
    if (tab === "overall") {
      setLoading(true);
      setError(null);
      setUsage(null);
      getUsageByRole("individual")
        .then(setUsage)
        .catch((err) => setError(err?.error || "Failed to fetch usage"))
        .finally(() => setLoading(false));
    }
  }, [tab]);

  useEffect(() => {
    if (tab === "per") {
      setUsersLoading(true);
      setUsersError(null);
      setUsers([]);
      setExpandedUserId(null);
      getAllIndividualUsersPaginated({ page: usersPage, limit: PAGE_SIZE })
        .then((res) => {
          setUsers(res?.data?.users || []);
          setUsersTotalPages(res?.data?.pagination?.totalPages || 1);
        })
        .catch(() => setUsersError("Failed to fetch users"))
        .finally(() => setUsersLoading(false));
    }
  }, [tab, usersPage]);

  useEffect(() => {
    if (tab === "per" && expandedUserId) {
      setPerUserLoading((prev) => ({ ...prev, [expandedUserId]: true }));
      setPerUserError((prev) => ({ ...prev, [expandedUserId]: null }));
      setPerUserUsage((prev) => ({ ...prev, [expandedUserId]: null }));
      getUsageByUserId(expandedUserId)
        .then((data) =>
          setPerUserUsage((prev) => ({ ...prev, [expandedUserId]: data }))
        )
        .catch(() =>
          setPerUserError((prev) => ({
            ...prev,
            [expandedUserId]: "Failed to fetch user usage",
          }))
        )
        .finally(() =>
          setPerUserLoading((prev) => ({ ...prev, [expandedUserId]: false }))
        );
    }
  }, [tab, expandedUserId]);

  // Fetch accurate usage data for all users when tab is "per"
  useEffect(() => {
    if (tab === "per" && users.length > 0) {
      users.forEach((user) => {
        getUserUsageAndPricing(user._id)
          .then((res) => {
            if (res?.data?.success) {
              setPerUserAccurateUsage((prev) => ({
                ...prev,
                [user._id]: res.data,
              }));
            }
          })
          .catch((err) => {
            console.error(`Failed to fetch accurate usage for user ${user._id}:`, err);
          });
      });
    }
  }, [tab, users]);

  // Format number helper
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || "0";
  };

  const stats = usage
    ? [
        {
          label: "Total Tokens",
          value: (
            (usage.totals?.highEndTokens || 0) +
            (usage.totals?.smallTokens || 0)
          ).toLocaleString(),
          colorVar: "--usage-bg-01",
          fgVar: "--usage-fg-01",
          unit: "tokens",
        },
        {
          label: "Total Cost",
          value: `$${usage.totals?.totalCost?.toFixed(6) || "0.00"}`,
          colorVar: "--usage-bg-02",
          fgVar: "--usage-fg-02",
          unit: "USD",
        },
        {
          label: "Total Requests",
          value: usage.totals?.totalRequests?.toLocaleString() || "0",
          colorVar: "--usage-bg-03",
          fgVar: "--usage-fg-03",
          unit: "requests",
        },
      ]
    : [];

  return (
    <div className="w-full flex justify-center p-6 h-full overflow-y-auto custom-scrollbar">
      <div className="max-w-7xl w-full">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            className={`py-1.5 px-4 text-sm font-medium transition-colors duration-150 border-none outline-none focus:outline-none rounded-lg ${
              tab === "overall"
                ? "bg-bg-40 text-fg-90 border-b-2 border-ac-02 shadow-sm"
                : "bg-transparent text-fg-60 hover:text-fg-80"
            }`}
            style={{
              boxShadow:
                tab === "overall" ? "0 1px 4px 0 rgba(0,0,0,0.03)" : "none",
            }}
            onClick={() => setTab("overall")}
          >
            Overall
          </button>
          <button
            className={`py-1.5 px-4 text-sm font-medium transition-colors duration-150 border-none outline-none focus:outline-none rounded-lg ${
              tab === "per"
                ? "bg-bg-40 text-fg-90 border-b-2 border-ac-02 shadow-sm"
                : "bg-transparent text-fg-60 hover:text-fg-80"
            }`}
            style={{
              boxShadow:
                tab === "per" ? "0 1px 4px 0 rgba(0,0,0,0.03)" : "none",
            }}
            onClick={() => setTab("per")}
          >
            Per Individual
          </button>
        </div>
        {/* Overall Tab */}
        {tab === "overall" &&
          (loading ? (
            <div className="text-fg-60 flex items-center justify-center h-[60vh] w-full">
              <span>Loading...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-[60vh] w-full text-red-500">
              {error}
            </div>
          ) : usage ? (
            <>
              <div className="w-full mb-6">
                <UsageStatsCards stats={stats} title="Individual Usage Stats" />
              </div>
              {/* Stats Tabs */}
              <div className="text-base font-semibold mb-4">Usage History</div>
              <div className="flex gap-2 mb-4">
                <button
                  className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                    statsTab === "daily"
                      ? "bg-bg-40 text-fg-90 shadow-sm"
                      : "bg-transparent text-fg-60 hover:text-fg-80"
                  }`}
                  onClick={() => setStatsTab("daily")}
                >
                  Daily
                </button>
                <button
                  className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                    statsTab === "weekly"
                      ? "bg-bg-40 text-fg-90 shadow-sm"
                      : "bg-transparent text-fg-60 hover:text-fg-80"
                  }`}
                  onClick={() => setStatsTab("weekly")}
                >
                  Weekly
                </button>
                <button
                  className={`py-1.5 px-4 text-sm font-medium rounded-lg ${
                    statsTab === "monthly"
                      ? "bg-bg-40 text-fg-90 shadow-sm"
                      : "bg-transparent text-fg-60 hover:text-fg-80"
                  }`}
                  onClick={() => setStatsTab("monthly")}
                >
                  Monthly
                </button>
              </div>
              {/* Stats Table */}
              <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-bd-50">
                  <thead className="bg-bg-40">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        {statsTab === "daily"
                          ? "Date"
                          : statsTab === "weekly"
                          ? "Week Range"
                          : "Month"}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Requests
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Tokens
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-bg-60 divide-y divide-bd-50">
                    {(statsTab === "daily"
                      ? usage.daily
                      : statsTab === "weekly"
                      ? usage.weekly
                      : usage.monthly) &&
                    (statsTab === "daily"
                      ? usage.daily
                      : statsTab === "weekly"
                      ? usage.weekly
                      : usage.monthly
                    ).length > 0 ? (
                      (statsTab === "daily"
                        ? usage.daily
                        : statsTab === "weekly"
                        ? usage.weekly
                        : usage.monthly
                      ).map((stat, idx) => (
                        <tr key={idx}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                            {statsTab === "daily"
                              ? stat.date
                              : statsTab === "weekly"
                              ? stat.weekRange
                              : stat.month}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                            {stat.requests?.toLocaleString() || "0"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                            {(
                              (stat.highEndTokens || 0) +
                              (stat.smallTokens || 0)
                            ).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                            ${stat.cost?.toFixed(6) || "0.000000"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-6 py-4 text-center text-sm text-fg-60"
                        >
                          No {statsTab} stats found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null)}
        {/* Per Individual Tab */}
        {tab === "per" && (
          <div>
            {usersLoading ? (
              <div className="flex items-center justify-center h-[40vh] w-full">
                <span>Loading users...</span>
              </div>
            ) : usersError ? (
              <div className="flex items-center justify-center h-[40vh] w-full text-red-500">
                {usersError}
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <h2 className="text-base font-semibold mb-2">
                    All Individuals
                  </h2>
                  <div className="overflow-x-auto bg-bg-60 rounded-lg">
                    <table className="min-w-full divide-y divide-bd-50 border border-gray-200 rounded-lg overflow-hidden">
                      <thead className="bg-bg-40">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                            Daily Usage
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                            Monthly Usage
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                            Total Usage
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-fg-60 uppercase tracking-wider">
                            Invoices
                          </th>
                          <th className="px-4 py-3 w-48 text-center text-xs font-medium text-fg-60 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-bg-60 divide-y divide-bd-50">
                        {users.length > 0 ? (
                          users.map((user) => {
                            const accurateUsage = perUserAccurateUsage[user._id];
                            const dailyTokens = accurateUsage?.dailyUsage?.accurateData?.totalTokens || 0;
                            const monthlyTokens = accurateUsage?.monthlyUsage?.accurateData?.totalTokens || 0;
                            const totalTokens = accurateUsage?.totalUsage?.totalTokens || 0;
                            
                            return (
                              <React.Fragment key={user._id}>
                                <tr>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                                    {user.name || "N/A"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                                    {user.email || "N/A"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                                    {formatNumber(dailyTokens)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                                    {formatNumber(monthlyTokens)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                                    {formatNumber(totalTokens)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                                    <div className="flex justify-center">
                                      <InvoiceDownloadButton 
                                        userId={user._id}
                                        invoices={perUserInvoices[user._id]}
                                        loading={perUserInvoicesLoading[user._id]}
                                        onLoadInvoices={() => {
                                          setPerUserInvoicesLoading((prev) => ({ ...prev, [user._id]: true }));
                                          getStripeInvoices(user._id)
                                            .then((res) => {
                                              if (res?.data?.success) {
                                                setPerUserInvoices((prev) => ({
                                                  ...prev,
                                                  [user._id]: res.data.invoices || [],
                                                }));
                                              }
                                            })
                                            .catch((err) => {
                                              console.error(`Failed to fetch invoices for user ${user._id}:`, err);
                                              setPerUserInvoices((prev) => ({
                                                ...prev,
                                                [user._id]: [],
                                              }));
                                            })
                                            .finally(() => {
                                              setPerUserInvoicesLoading((prev) => ({ ...prev, [user._id]: false }));
                                            });
                                        }}
                                      />
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-fg-50">
                                  <div className="flex justify-end gap-2 items-center action-buttons-row w-full">
                                    <button
                                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-ac-02 text-white hover:bg-ac-01 transition-all duration-200 text-xs font-medium shadow-sm hover:shadow ${
                                        expandedUserId === user._id
                                          ? "ring-2 ring-ac-02 ring-offset-1"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        setExpandedUserId(
                                          expandedUserId === user._id
                                            ? null
                                            : user._id
                                        )
                                      }
                                      aria-expanded={
                                        expandedUserId === user._id
                                      }
                                      aria-controls={`usage-row-${user._id}`}
                                    >
                                      <span>{expandedUserId === user._id
                                        ? "Hide Usage"
                                        : "View Usage"}</span>
                                      {expandedUserId === user._id ? (
                                        <ChevronDown
                                          size={14}
                                          className="flex-shrink-0"
                                        />
                                      ) : (
                                        <ChevronRightIcon
                                          size={14}
                                          className="flex-shrink-0"
                                        />
                                      )}
                                    </button>
                                    <div className="flex gap-1.5 ml-2">
                                      {/* Download Invoice PDF Button */}
                                      <button
                                        className="p-2 rounded-lg text-fg-70 hover:text-fg-90 hover:bg-bg-40 active:bg-bg-30 transition-all duration-200 flex items-center justify-center border border-transparent hover:border-bd-50 shadow-sm hover:shadow"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const response =
                                              await downloadInvoicePdf(
                                                user._id
                                              );
                                            const blob = new Blob(
                                              [response.data],
                                              { type: "application/pdf" }
                                            );
                                            const url =
                                              window.URL.createObjectURL(blob);
                                            const link =
                                              document.createElement("a");
                                            link.href = url;
                                            link.setAttribute(
                                              "download",
                                              `invoice-${user._id}.pdf`
                                            );
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            window.URL.revokeObjectURL(url);
                                          } catch (error) {
                                            alert(
                                              "Failed to download invoice PDF."
                                            );
                                          }
                                        }}
                                        title="Download Invoice PDF"
                                      >
                                        <ArrowDownToLine size={16} />
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                              {expandedUserId === user._id && (
                                <tr key={`usage-row-${user._id}`}>
                                  {" "}
                                  {/* No border for expanded row */}
                                  <td
                                    colSpan="3"
                                    className="p-0 border-none bg-transparent"
                                  >
                                    <UsageBentoCard
                                      loading={perUserLoading[user._id]}
                                      error={perUserError[user._id]}
                                      usage={perUserUsage[user._id]}
                                    />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })) : (
                          <tr>
                            <td
                              colSpan="7"
                              className="text-center text-fg-60 py-8"
                            >
                              No users found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  <div className="flex justify-center items-center gap-2 mt-4 pb-6">
                    <button
                      className="p-1 rounded disabled:opacity-50"
                      onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                      disabled={usersPage === 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <span className="px-2 text-base font-semibold">
                      {usersPage}
                    </span>
                    <button
                      className="p-1 rounded disabled:opacity-50"
                      onClick={() =>
                        setUsersPage((p) => Math.min(usersTotalPages, p + 1))
                      }
                      disabled={usersPage === usersTotalPages}
                      aria-label="Next page"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IndividualsUsageTab;
